import { Column, Table, Button } from '@cb/apricot-react';
import { FC } from 'react';

import { TSchool, ISearchRecord, ISummary, IAIUpdateRequest, IOrgAI, IPhone } from 'src/types';
import { findOrgById } from 'src/apis';
import { getFile } from 'src/services/schoolRequest';


// TO-DO: Referencing the other to-do on the parent
// need to create an interface for the data of an
// existing AI for the update form to ingest.  Translate
// at the API layer in the response before returning.
interface SchoolSearchResultProps {
  schools: ISearchRecord[];
  onOpen?: (data: any) => void;
  onUpgrade?: (data: IOrgAI) => void;
}

const SchoolSearchResult: FC<SchoolSearchResultProps> = ({ schools, onOpen, onUpgrade }) => {
  const isInternationalSchool = (school: TSchool): boolean => {
    return school.orgCountryCode !== 'US' && school.orgCountryCode !== '';
  };

  const mapAI = async (school: TSchool): Promise<IOrgAI> => {
    const record = await findOrgById(school.orgId);

    const phone = record?.org?.phone;
    let formattedPhone = '';
    if (phone) {
      formattedPhone = `+${phone.phoneCc || '1'} (${phone.phoneNdc || ''}) ${phone.phoneLocal || ''}`;
    }
      const fax = record?.org?.fax;
      let formattedFax = '';
      if (fax) {
          formattedFax = `+${fax.phoneCc || '1'} (${fax.phoneNdc || ''}) ${fax.phoneLocal || ''}`;
      }

    const schoolAi: IOrgAI = {
      ...record.org,
      phone: phone ? {
        phoneCc: phone.phoneCc,
        phoneNdc: phone.phoneNdc,
        phoneLocal: phone.phoneLocal,
        formatted: formattedPhone
      } : {} as IPhone,
      aiCode: school.aiCode,
      aiStatusCd: school.aiStatusCd,
      aiLevelCd: school.aiLevelCd,
      aiCategoryCd: school.aiCategoryCd,
      fax: fax ? {
        phoneCc: fax.phoneCc,
        phoneNdc: fax.phoneNdc,
        phoneLocal: fax.phoneLocal,
        formatted: formattedFax
    } : {} as IPhone
    };
    return schoolAi;
  };

  const openAI = async (school: TSchool) => {

    /**
     *  Merge in missing data to the school record distilled from the search data already retrieved.
     *
     * I would much prefer there be a unified endpoint to retrieve data for an AI by id which contains
     * the full data structure.  Putting the data stitching on the front end seems less than ideal given
     * this record is supposed to be concretely defined, meaning the backend should hold the definition
     * in order to ensure proper schema distribution to dependent apps.
     */
    const schoolAi = await mapAI(school);
    if (onOpen) {
      onOpen({ data: schoolAi });
    }
  };

  const updateRequest = async (summary: ISummary) => {
    if (!summary.submission) throw new Error("Cannot find submission");
    const update: IAIUpdateRequest = await getFile(summary.submission);
    if (onOpen) {
      onOpen(update);
    }
  };

  const upgradeOrg = async (school: TSchool) => {
    const schoolAi = await mapAI(school);
    if (onUpgrade) {
      onUpgrade(schoolAi);
    }
  };

  return (
    <Table
      a11yHighlightType="append"
      sortType="inline"
      className="header-aligned-table"
      data={schools}
      tableId="schoolSearchResult">
      <Column field="id" rowHeader title=""
        onValueFormat={(_value, record: any, rowIndex) => {
          const data = record.ai;
          const activeRequest = record.active;
          const ownedRequest = record.owned;
          const isActive = data.aiStatusCd === '2';
          const isComplete = data.aiLevelCd;

          if (!isActive) {
            return <Button onClick={() => { upgradeOrg(data); }}>Create</Button>;
          } else if (isComplete && !activeRequest) {
            return <Button onClick={() => { openAI(data); }}>Update</Button>;
          } else if (activeRequest && ownedRequest) {
            return <Button onClick={() => { updateRequest(ownedRequest); }}>Update Requested</Button>;
          } else if (activeRequest && !ownedRequest) {
            return <Button disabled={true}>Request Processing</Button>;
          } else {
            return <span style={{ color: 'red' }}>Update Unavailable</span>;
          }
        }} />
      <Column field="ai.aiCode" sortable title="School Code" onValueFormat={(_value, record: any) => {
        const data = record.ai;
        return data.aiCode;
      }} />
      <Column
        field="ai.aiName"
        sortable
        title="School Name And Address"
        onValueFormat={(_value, record: any) => {
          const data = record.ai;
          return <>
            <div>
              <strong>Name:</strong> {data.aiName}
            </div>
            <div>
              <strong>Address:</strong> {data.orgAddress}
            </div>
            <div>
              <strong>City:</strong> {data.orgCity}
            </div>
            {isInternationalSchool(data) && (
              <div>
                <strong>Province:</strong> {data.orgProvince}
              </div>
            )}
            {isInternationalSchool(data) && (
              <div>
                <strong>Postal Code:</strong> {data.orgPostalCode}
              </div>
            )}
            {!isInternationalSchool(data) && (
              <div>
                <strong>State:</strong> {data.orgStateCode}
              </div>
            )}
            {!isInternationalSchool(data) && (
              <div>
                <strong>Zip:</strong> {data.orgZip}
              </div>
            )}
          </>;
        }}
      />
      <Column field="aiStatus" sortable title="School Status" onValueFormat={(_value, record: any) => {
        const data = record.ai;
        return data.aiStatus;
      }} />
      <Column field="aiLevelCd" sortable title="School Level Code" onValueFormat={(_value, record: any) => {
        const data = record.ai;
        return <div>
          <div>{data.aiLevelCd}</div>
          {data.aiLevelCd === "1" &&
            <Button onClick={() => { upgradeOrg(data); }}>Update to Level II</Button>
          }
        </div>;
      }} />
    </Table>
  );
};

export default SchoolSearchResult;
